import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import * as _ from 'lodash-es';

/**
 * Section 965 Angular service.
 * Replicates Section965Factory from AngularJS (Section965Service.js).
 * Action codes: xgjjqa (saveAmtsComments), c81qt9 (saveFilingGroup),
 * xwii43 (saveNovDecAdjs), vzteme (saveEP1231Adjs), q8fdvr (getFilingGroups).
 */
@Injectable({
  providedIn: 'root'
})
export class Section965Service {

  constructor(private http: HttpClient) {}

  /**
   * Load filing groups (action_code: q8fdvr).
   * Returns observable of the array (jsonObject from response), or empty array on error.
   */
  getFilingGroups(
    baseUrl: string,
    scrFilingGroup: string,
    globalService: any,
    filterParams: any = {}
  ): Observable<any[]> {
    const params: Record<string, string> = {
      action_code: 'q8fdvr',
      scr_filing_group: scrFilingGroup || ''
    };
    const merged = _.merge({}, globalService?.globalParams || {}, filterParams, params);
    const urlEncoded = new URLSearchParams();
    Object.keys(merged).forEach(key => {
      if (merged[key] !== undefined && merged[key] !== null) {
        urlEncoded.set(key, String(merged[key]));
      }
    });
    const url = `${baseUrl}/loadJsonObject?${urlEncoded.toString()}`;
    return this.http.get<any>(url, { withCredentials: true }).pipe(
      map((data: any) => {
        const arr = data?.jsonObject != null ? data.jsonObject : (Array.isArray(data) ? data : []);
        return Array.isArray(arr) ? arr : [];
      })
    );
  }

  /**
   * Save adjustment amounts and comments (action_code: xgjjqa).
   */
  saveAmtsComments(
    baseUrl: string,
    data: { ghost_scenario: string; leid: string; tax_code: string; filing_group: string; adj_amt: number | string; comments: string },
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=xgjjqa`;
    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    params = _.extend({}, params, {
      ghost_scenario: data.ghost_scenario,
      leid: data.leid,
      tax_code: data.tax_code,
      filing_group: data.filing_group,
      adj_amt: data.adj_amt,
      comments: data.comments
    });
    return this.postForm(url, params);
  }

  /**
   * Save filing group (action_code: c81qt9).
   */
  saveFilingGroup(
    baseUrl: string,
    data: { ghost_scenario: string; leid: string; tax_code: string; filing_group: string; new_filing_group: string },
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=c81qt9`;
    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    params = _.extend({}, params, {
      ghost_scenario: data.ghost_scenario,
      leid: data.leid,
      tax_code: data.tax_code,
      filing_group: data.filing_group,
      new_filing_group: data.new_filing_group
    });
    return this.postForm(url, params);
  }

  /**
   * Save Nov/Dec adjustments (action_code: xwii43).
   */
  saveNovDecAdjs(
    baseUrl: string,
    data: {
      ghost_scenario: string; leid: string; tax_code: string; filing_group: string;
      adj_type: string; nov_adj_amt: number | string; nov_adj_comments: string;
      dec_adj_amt: number | string; dec_adj_comments: string;
    },
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=xwii43`;
    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    params = _.extend({}, params, {
      ghost_scenario: data.ghost_scenario,
      leid: data.leid,
      tax_code: data.tax_code,
      filing_group: data.filing_group,
      adj_type: data.adj_type,
      nov_adj_amt: data.nov_adj_amt,
      nov_adj_comments: data.nov_adj_comments || '',
      dec_adj_amt: data.dec_adj_amt,
      dec_adj_comments: data.dec_adj_comments || ''
    });
    return this.postForm(url, params);
  }

  /**
   * Save 12/31 E&P adjustments (action_code: vzteme).
   */
  saveEP1231Adjs(
    baseUrl: string,
    data: {
      ghost_scenario: string; leid: string; tax_code: string; filing_group: string;
      ep_1231adj_amt: number | string; ep_1231adj_comments: string;
    },
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=vzteme`;
    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    params = _.extend({}, params, {
      ghost_scenario: data.ghost_scenario,
      leid: data.leid,
      tax_code: data.tax_code,
      filing_group: data.filing_group,
      ep_1231adj_amt: data.ep_1231adj_amt,
      ep_1231adj_comments: data.ep_1231adj_comments || ''
    });
    return this.postForm(url, params);
  }

  /**
   * Load Section 965 Tax Statement rows (action_code: eixvqj).
   */
  getTaxStatementData(
    baseUrl: string,
    globalService: any,
    filterParams: any = {}
  ): Observable<any[]> {
    const params: Record<string, string> = {
      action_code: 'eixvqj'
    };
    const merged = _.merge({}, globalService?.globalParams || {}, filterParams, params);
    const urlEncoded = new URLSearchParams();
    Object.keys(merged).forEach((key) => {
      if (merged[key] !== undefined && merged[key] !== null) {
        urlEncoded.set(key, String(merged[key]));
      }
    });
    const url = `${baseUrl}/loadJsonObject?${urlEncoded.toString()}`;
    return this.http.get<any>(url, { withCredentials: true }).pipe(
      map((data: any) => {
        const arr = data?.jsonObject != null ? data.jsonObject : (Array.isArray(data) ? data : []);
        return Array.isArray(arr) ? arr : [];
      })
    );
  }

  /**
   * Save Section 965 Tax Statement row/election values (action_code: pxi6x7).
   */
  saveTaxStatementData(
    baseUrl: string,
    data: {
      ghost_scenario: number | string;
      row_order: number | string;
      total_amt: number | string;
      ge_total_amt: number | string;
      gecs_total_amt: number | string;
      election_type: string;
    },
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=pxi6x7`;
    let params = _.merge({}, globalService?.globalParams || {}, filterParams);
    params = _.extend({}, params, {
      ghost_scenario: data.ghost_scenario,
      row_order: data.row_order,
      total_amt: data.total_amt,
      ge_total_amt: data.ge_total_amt,
      gecs_total_amt: data.gecs_total_amt,
      election_type: data.election_type
    });
    return this.postForm(url, params);
  }

  private postForm(url: string, params: Record<string, unknown>): Observable<any> {
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, String(params[key]));
      }
    });
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    return this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true });
  }
}
